import { useState, useEffect, useMemo } from "react";
import { RxType } from "@/types/enums";
import { Rx } from "@/types/rx";
import { Feature, FeatureCollection, GeoJSON } from "geojson";
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    SharedSelection,
    Slider,
    Input,
    Switch,
    useDisclosure,
    Modal,
    ModalContent,
    ModalBody,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDroplet,
    faDropletSlash,
    faCheck,
    faXmark,
    faAngleDown,
    faFilterCircleXmark,
    faRefresh,
    faMap,
    faFillDrip,
} from "@fortawesome/free-solid-svg-icons";
import debounce from "lodash.debounce";
import { UseMutationResult } from "@tanstack/react-query";
import useRequest from "@/lib/request/useRequest";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import SimulateRx from "./SimulateRx";

import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    updateLayerOpacity,
    updateLayerVisibility,
    moveLayersToTop,
} from "@/lib/utils/map";
import {
    RX_SOURCE,
    RX_FILL_LAYER,
    RX_LINE_LAYER,
    RX_PLOT_LABEL,
    RX_HIGHLIGHT_SOURCE,
    RX_HIGHLIGHT_FILL_LAYER,
    RX_HIGHLIGHT_LINE_LAYER,
} from "@/lib/map/layers";
import { useTranslation } from "react-i18next";
import { AxiosRequestConfig } from "axios";

interface RxTableFiltersProps {
    rxId: number | string;
    zones: number[];
    treatments: string[];
    rxType?: RxType;
    zoneFilter: SharedSelection;
    onZoneFilterChange: (filter: SharedSelection) => void;
    treatmentFilter: SharedSelection;
    onTreatmentFilterChange: (filter: SharedSelection) => void;
    irrigatedFilter: SharedSelection;
    onIrrigatedFilterChange: (filter: SharedSelection) => void;
    endGunFilter: SharedSelection;
    onEndGunFilterChange: (filter: SharedSelection) => void;
    table: FeatureCollection;
    plots: FeatureCollection;
    selectedFeatures: SharedSelection;
    hoveredFeatureId: string | number | null;
    filteredItems: Feature[];
    updateRatesRequest: UseMutationResult<Rx, Error, AxiosRequestConfig>;
    handleUpdateSuccess: (updatedRx: Rx) => void;
    isBiomassProduct: boolean;
}

function RxTableToolbar({
    rxId,
    zones,
    treatments,
    rxType,
    zoneFilter,
    treatmentFilter,
    irrigatedFilter,
    endGunFilter,
    onZoneFilterChange,
    onTreatmentFilterChange,
    onIrrigatedFilterChange,
    onEndGunFilterChange,
    table,
    plots,
    selectedFeatures,
    hoveredFeatureId,
    filteredItems,
    updateRatesRequest,
    handleUpdateSuccess,
    isBiomassProduct,
}: RxTableFiltersProps) {
    const { t } = useTranslation();
    const { useMutateRequest } = useRequest();
    const { map, mapLoaded } = useMap();
    const [mapRx, setMapRx] = useState<boolean>(true);
    const [plotsOnMap, setPlotsOnMap] = useState<boolean>(true);
    const [mapRxOpacity, setMapRxOpacity] = useState<number | number[]>(0.8);
    const { isOpen, onOpen, onOpenChange } = useDisclosure();

    const rateToUpdateOptions = useMemo(() => {
        return isBiomassProduct
            ? [{ value: "product", label: "Product" }]
            : [
                  { value: "n", label: "N" },
                  { value: "product", label: "Product" },
              ];
    }, [isBiomassProduct]);

    const mapFeatures: GeoJSON = useMemo(() => {
        return {
            type: "FeatureCollection",
            features:
                !mapRx || !table?.features
                    ? []
                    : plotsOnMap
                      ? table.features.concat(
                            (plots?.features ?? []).map((feature: Feature) => {
                                return {
                                    ...feature,
                                    properties: {
                                        ...feature.properties,
                                        target_rate_n:
                                            feature.properties?.n_rate,
                                    },
                                };
                            }),
                        )
                      : table.features,
        };
    }, [mapRx, table?.features, plotsOnMap, plots?.features]);

    const [minRate, midRate, maxRate] = useMemo(() => {
        if (!mapFeatures || !mapFeatures.features) return [0, 0, 0];
        const rates = mapFeatures.features.map((feature: Feature) => {
            const rate = feature.properties?.target_rate_n;
            return rate !== undefined ? rate : 0;
        });
        let min = Math.floor(Math.min(...rates));
        const max = Math.ceil(Math.max(...rates));
        if (max === min) {
            min = 0;
        }
        const mid = (min + max) / 2;
        return [min, mid, max];
    }, [mapFeatures]);

    useEffect(() => {
        if (!map || !mapLoaded || !mapRx) return;
        addUpdateGeojsonSource(map, RX_SOURCE, {
            type: "geojson",
            data: mapFeatures,
            tolerance: 0,
        });
        addUpdateGeojsonLayers(map, [
            RX_FILL_LAYER,
            RX_LINE_LAYER,
            RX_PLOT_LABEL,
        ]);
        updateLayerOpacity(map, [RX_FILL_LAYER.id, RX_LINE_LAYER.id], 0.8);
        updateLayerVisibility(
            map,
            [RX_FILL_LAYER.id, RX_LINE_LAYER.id, RX_PLOT_LABEL.id],
            true,
        );
        moveLayersToTop(map, [
            RX_FILL_LAYER.id,
            RX_LINE_LAYER.id,
            RX_PLOT_LABEL.id,
        ]);
        map.setPaintProperty(RX_FILL_LAYER.id, "fill-color", [
            "interpolate",
            ["linear"],
            ["get", "target_rate_n"],
            minRate,
            "#ff0000",
            midRate,
            "#ffff00",
            maxRate,
            "#00ff00",
        ]);
        const selectedIds =
            typeof selectedFeatures === "object"
                ? Array.from(selectedFeatures)
                : [];
        const highlightedIds = new Set<string | number>(selectedIds);
        if (hoveredFeatureId != null) {
            highlightedIds.add(hoveredFeatureId);
        }
        addUpdateGeojsonSource(map, RX_HIGHLIGHT_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: filteredItems.filter(
                    (feature: Feature) =>
                        feature?.id != null && highlightedIds.has(feature.id),
                ),
            },
        });
        addUpdateGeojsonLayers(map, [
            RX_HIGHLIGHT_FILL_LAYER,
            RX_HIGHLIGHT_LINE_LAYER,
        ]);
        updateLayerVisibility(
            map,
            [RX_HIGHLIGHT_FILL_LAYER.id, RX_HIGHLIGHT_LINE_LAYER.id],
            highlightedIds.size > 0,
        );
        moveLayersToTop(map, [
            RX_HIGHLIGHT_FILL_LAYER.id,
            RX_HIGHLIGHT_LINE_LAYER.id,
        ]);
    }, [
        map,
        mapLoaded,
        mapRx,
        table,
        minRate,
        midRate,
        maxRate,
        selectedFeatures,
        hoveredFeatureId,
        filteredItems,
        plotsOnMap,
        mapFeatures,
    ]);

    useEffect(() => {
        return () => {
            if (!map || !mapLoaded) return;
            removeLayersIfExists(map, [
                RX_FILL_LAYER.id,
                RX_LINE_LAYER.id,
                RX_PLOT_LABEL.id,
            ]);
            removeSourcesIfExists(map, [RX_SOURCE]);
        };
    }, [map, mapLoaded]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        updateLayerVisibility(
            map,
            [
                RX_FILL_LAYER.id,
                RX_LINE_LAYER.id,
                RX_PLOT_LABEL.id,
                RX_HIGHLIGHT_FILL_LAYER.id,
                RX_HIGHLIGHT_LINE_LAYER.id,
            ],
            mapRx,
        );
        if (mapRx) {
            moveLayersToTop(map, [
                RX_FILL_LAYER.id,
                RX_LINE_LAYER.id,
                RX_PLOT_LABEL.id,
                RX_HIGHLIGHT_FILL_LAYER.id,
                RX_HIGHLIGHT_LINE_LAYER.id,
            ]);
        }
    }, [map, mapLoaded, mapRx]);

    useEffect(() => {
        if (!map || !mapLoaded) return;
        updateLayerOpacity(map, [RX_FILL_LAYER.id], mapRxOpacity as number);
    }, [map, mapLoaded, mapRxOpacity]);

    const [rateToUpdate, setRateToUpdate] = useState<SharedSelection>(
        new Set(isBiomassProduct ? ["product"] : ["n"]),
    );
    const [bulkRate, setBulkRate] = useState("");

    const handleBulkUpdate = debounce(() => {
        if (!bulkRate) return;
        const features =
            Array.from(selectedFeatures).length > 0
                ? Array.from(selectedFeatures)
                : filteredItems.map((feature: Feature) => feature.id);
        updateRatesRequest.mutate(
            {
                data: {
                    rate: Array.from(rateToUpdate).includes("n")
                        ? "n"
                        : "product",
                    rows: features.map((featureId) => ({
                        row: Number(featureId),
                        rate: Number(bulkRate),
                    })),
                },
            },
            {
                onSuccess: (updatedRx: Rx) => {
                    handleUpdateSuccess(updatedRx);
                },
            },
        );
    }, 500);

    const handleLimitRates = debounce((values: number | number[]) => {
        if (!Array.isArray(values)) return;
        const minRate = values[0];
        const maxRate = values[1];
        const features =
            Array.from(selectedFeatures).length > 0
                ? filteredItems.filter((feature: Feature) => {
                      return (
                          !feature?.id ||
                          Array.from(selectedFeatures).includes(feature.id)
                      );
                  })
                : filteredItems;
        const propertyToUpdate = Array.from(rateToUpdate).includes("n")
            ? "target_rate_n"
            : "product_rate";
        const featuresToUpdate = features
            .filter((feature: Feature) => {
                const rate = feature.properties?.[propertyToUpdate];
                if (rate === undefined || rate === null) return false;
                return rate < minRate || rate > maxRate;
            })
            .map((feature: Feature) => ({
                row: Number(feature.id),
                rate:
                    feature.properties?.[propertyToUpdate] <= minRate
                        ? minRate
                        : maxRate,
            }));
        if (featuresToUpdate.length === 0) return;
        updateRatesRequest.mutate(
            {
                data: {
                    rate:
                        propertyToUpdate === "target_rate_n" ? "n" : "product",
                    rows: featuresToUpdate,
                },
            },
            {
                onSuccess: (updatedRx: Rx) => {
                    handleUpdateSuccess(updatedRx);
                },
            },
        );
    }, 500);

    const recreateRx = useMutateRequest<Rx>({
        url: `/prescriptions/${rxId}/recreate/`,
        method: "put",
        successMessage: t("success.recreated_rx", { ns: "messages" }),
        errorMessage: t("error.failed_to_recreate_rx", { ns: "messages" }),
    });
    const resetRates = () => {
        recreateRx.mutate(
            {},
            {
                onSuccess: (updatedRx: Rx) => {
                    handleUpdateSuccess(updatedRx);
                },
            },
        );
    };

    return (
        <>
            <div className="flex flex-col w-full px-2">
                <Button
                    color="primary"
                    variant="flat"
                    onPress={onOpen}
                    className="my-2"
                >
                    <img
                        src={allNHorizontalImage}
                        alt="All N Horizontal"
                        className="h-full object-cover"
                    />
                    {t("simulate_rx", { ns: "rx" })}
                </Button>
                <div className="flex flex-row items-center justify-between">
                    <Button
                        size="sm"
                        color="warning"
                        variant="faded"
                        onPress={resetRates}
                        isLoading={recreateRx.isPending}
                    >
                        <FontAwesomeIcon icon={faRefresh} />
                        {t("recreate_rx", { ns: "rx" })}
                    </Button>
                    <div className="flex flex-row items-center gap-3">
                        {mapRx && (
                            <div className="flex flex-col items-center w-[100px]">
                                <Slider
                                    size="sm"
                                    hideValue={true}
                                    minValue={0}
                                    maxValue={1}
                                    step={0.01}
                                    value={mapRxOpacity}
                                    onChange={setMapRxOpacity}
                                />
                                <div className="bg-gradient-to-r from-[#FF0000] via-[#FFFF00] to-[#006400] w-full h-3 mt-1 rounded-xl"></div>
                                <div className="flex flex-row justify-evenly rounded-xl text-xs w-full">
                                    <span className="mr-auto">{minRate}</span>
                                    <span className="mx-auto">{midRate}</span>
                                    <span className="ml-auto">{maxRate}</span>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col items-center gap-2">
                            <Button
                                color={!mapRx ? "primary" : "default"}
                                size="sm"
                                onPress={setMapRx.bind(null, !mapRx)}
                                variant="faded"
                            >
                                <FontAwesomeIcon icon={faMap} />
                                {mapRx
                                    ? t("hide_rx", { ns: "rx" })
                                    : t("map_rx", { ns: "rx" })}
                            </Button>
                            {(plots?.features?.length ?? 0) > 0 && mapRx && (
                                <Switch
                                    isSelected={plotsOnMap}
                                    onValueChange={setPlotsOnMap}
                                    size="sm"
                                >
                                    {t("plots", { ns: "rx" })}
                                </Switch>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-row w-full gap-2 p-1 items-end justify-between">
                    <div className="pb-1 flex flex-row gap-2">
                        <Button
                            size="sm"
                            isIconOnly={true}
                            onPress={() => {
                                onZoneFilterChange("all");
                                onTreatmentFilterChange("all");
                                onIrrigatedFilterChange("all");
                                onEndGunFilterChange("all");
                            }}
                        >
                            <FontAwesomeIcon icon={faFilterCircleXmark} />
                        </Button>
                        {zones.length > 0 && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        variant="flat"
                                        endContent={
                                            <FontAwesomeIcon
                                                icon={faAngleDown}
                                            />
                                        }
                                    >
                                        {t("zones", { ns: "rx" })}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    disallowEmptySelection={true}
                                    aria-label="Zone Filter"
                                    closeOnSelect={false}
                                    selectedKeys={zoneFilter}
                                    selectionMode="multiple"
                                    onSelectionChange={onZoneFilterChange}
                                >
                                    {zones.map((zone) => (
                                        <DropdownItem key={zone}>
                                            {zone}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        {treatments.length > 0 && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        variant="flat"
                                        endContent={
                                            <FontAwesomeIcon
                                                icon={faAngleDown}
                                            />
                                        }
                                    >
                                        {t("treatments", { ns: "rx" })}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    disallowEmptySelection={true}
                                    aria-label="Treatment Filter"
                                    closeOnSelect={false}
                                    selectedKeys={treatmentFilter}
                                    selectionMode="multiple"
                                    onSelectionChange={onTreatmentFilterChange}
                                >
                                    {treatments.map((treatment) => (
                                        <DropdownItem key={treatment}>
                                            {treatment}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        {rxType === RxType.ZONE && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        variant="flat"
                                        endContent={
                                            <FontAwesomeIcon
                                                icon={faAngleDown}
                                            />
                                        }
                                    >
                                        {t("irrigated", { ns: "rx" })}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    disallowEmptySelection={true}
                                    aria-label="Irrigated Filter"
                                    closeOnSelect={false}
                                    selectedKeys={irrigatedFilter}
                                    selectionMode="multiple"
                                    onSelectionChange={onIrrigatedFilterChange}
                                >
                                    <DropdownItem key="true">
                                        <FontAwesomeIcon
                                            icon={faDroplet}
                                            className="text-blue-500"
                                        />
                                        <span className="ml-2">
                                            {t("irrigated", { ns: "rx" })}
                                        </span>
                                    </DropdownItem>
                                    <DropdownItem key="false">
                                        <FontAwesomeIcon
                                            icon={faDropletSlash}
                                            className="text-gray-500"
                                        />
                                        <span className="ml-2">
                                            {t("non_irrigated", { ns: "rx" })}
                                        </span>
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        {rxType !== RxType.ZONE && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        size="sm"
                                        color="primary"
                                        variant="flat"
                                        endContent={
                                            <FontAwesomeIcon
                                                icon={faAngleDown}
                                            />
                                        }
                                    >
                                        {t("end_gun", { ns: "rx" })}
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu
                                    disallowEmptySelection={true}
                                    aria-label="End Gun Filter"
                                    closeOnSelect={false}
                                    selectedKeys={endGunFilter}
                                    selectionMode="multiple"
                                    onSelectionChange={onEndGunFilterChange}
                                >
                                    <DropdownItem key="true">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="text-green-500"
                                        />
                                        <span className="ml-2">
                                            {t("on", { ns: "rx" })}
                                        </span>
                                    </DropdownItem>
                                    <DropdownItem key="false">
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            className="text-red-500"
                                        />
                                        <span className="ml-2">
                                            {t("off", { ns: "rx" })}
                                        </span>
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        )}
                    </div>
                    <div className="flex flex-row gap-2 w-full items-end">
                        <Input
                            label={t("batch_fill_rates", { ns: "rx" })}
                            labelPlacement="outside"
                            placeholder={t("rate", { ns: "rx" })}
                            variant="bordered"
                            size="sm"
                            classNames={{
                                inputWrapper: "p-1 h-10 max-w-[200px]",
                            }}
                            value={bulkRate}
                            onValueChange={setBulkRate}
                            startContent={
                                <Dropdown>
                                    <DropdownTrigger>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            variant="flat"
                                            endContent={
                                                <FontAwesomeIcon
                                                    icon={faAngleDown}
                                                />
                                            }
                                        >
                                            {Array.from(rateToUpdate).includes(
                                                "n",
                                            )
                                                ? "N"
                                                : "Product"}
                                        </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu
                                        aria-label="Rate Filter"
                                        disallowEmptySelection={true}
                                        selectedKeys={rateToUpdate}
                                        closeOnSelect={true}
                                        selectionMode="single"
                                        onSelectionChange={setRateToUpdate}
                                    >
                                        {rateToUpdateOptions.map((option) => (
                                            <DropdownItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            endContent={
                                <Button
                                    size="sm"
                                    color="primary"
                                    variant="solid"
                                    isIconOnly={true}
                                    onPress={handleBulkUpdate}
                                >
                                    <FontAwesomeIcon icon={faFillDrip} />
                                </Button>
                            }
                            onBlur={handleBulkUpdate}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleBulkUpdate();
                                }
                            }}
                        />
                        <Slider
                            label={t("limit_rates", { ns: "rx" })}
                            size="sm"
                            className="max-w-md pb-3"
                            defaultValue={[0, 200]}
                            minValue={0}
                            maxValue={200}
                            onChangeEnd={handleLimitRates}
                        />
                    </div>
                </div>
            </div>
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="2xl"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalBody>
                        <SimulateRx />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default RxTableToolbar;
